import { TRUNCATE_SIZE_NAME } from '@components/map/layers/sensors/sensor.mapper';
import { truncateMessage } from '@utils/locale.utils';
import {
  getAutomatonSectionFeatureId,
  getAutomatonSectionKeys,
  getAutomatonSectionSpriteIconName,
  getSectionCenterCoordinates,
  getSectionIconStatus,
  getSectionLineColor,
  getSectionName,
  getSectionPositions,
  toLinePointsFeature,
} from '@utils/map/automatonSection.utils';
import { ColorLabelEnum, hexColor } from '@utils/map/map.constants';
import { SHOW_AUTOMATON_SECTION_MAX_ZOOM } from '@utils/map/mapZoom.constants';
import { toLonLatArray } from '@utils/validation/coordinates.utils';
import { Feature, Position } from 'geojson';

import { FeatureTypeEnum } from '@/types/map.types';
import {
  AutomatonPortalWithStatus,
  AutomatonSectionData,
  AutomatonSectionStatus,
  AutomatonSectionStatusEnumWithMaintenance,
  SegmentSpecificStateEnum,
} from '@/types/sensor/sensors';

function toAutomatonSectionIcons(
  sectionData: AutomatonSectionData,
  position: Position,
  iconStatus: AutomatonSectionStatusEnumWithMaintenance,
  specificState: SegmentSpecificStateEnum | null,
): Feature {
  const automatonsSectionNames = getAutomatonSectionKeys(sectionData);
  return {
    type: 'Feature',
    id: `${FeatureTypeEnum.AUTOMATON_SECTION_ICON}-${getAutomatonSectionFeatureId(automatonsSectionNames)}`,
    properties: {
      featureType: FeatureTypeEnum.AUTOMATON_SECTION_ICON,
      value: automatonsSectionNames,
      icon: getAutomatonSectionSpriteIconName(iconStatus, specificState),
      textField: automatonsSectionNames.sectionName,
      selectOptionLabel: automatonsSectionNames.sectionName,
    },
    geometry: {
      type: 'Point',
      coordinates: position,
    },
  };
}

export function toAutomatonSectionLabelFeature(sectionData: AutomatonSectionData, position: Position): Feature {
  const automatonsSectionNames = getAutomatonSectionKeys(sectionData);
  return {
    type: 'Feature',
    id: `${FeatureTypeEnum.AUTOMATON_SECTION_LABEL}-${getAutomatonSectionFeatureId(automatonsSectionNames)}`,
    properties: {
      featureType: FeatureTypeEnum.AUTOMATON_SECTION_LABEL,
      value: automatonsSectionNames,
      textField: truncateMessage(automatonsSectionNames.sectionName, TRUNCATE_SIZE_NAME),
      tooltipAnchor: 'pointer',
      showTooltip: automatonsSectionNames.sectionName.length > TRUNCATE_SIZE_NAME,
      selectOptionLabel: automatonsSectionNames.sectionName,
    },
    geometry: {
      type: 'Point',
      coordinates: position,
    },
  };
}

export const toAutomatonSectionLineAndPointsFeatures = (
  automatonSectionData: AutomatonSectionData[],
  statuses: AutomatonSectionStatus[],
  zoom: number | undefined,
): Feature[] =>
  automatonSectionData.flatMap((sectionData) => {
    const automatonsSectionNames = getAutomatonSectionKeys(sectionData);
    let sectionPositions = getSectionPositions(sectionData.section);

    if (sectionPositions.length < 2) {
      return [];
    }

    if (zoom && zoom < SHOW_AUTOMATON_SECTION_MAX_ZOOM) {
      sectionPositions = [sectionPositions[0], sectionPositions[sectionPositions.length - 1]];
    }

    const positionsCount = sectionPositions.length;

    const sectionStatus = statuses.find((status) => status.name === automatonsSectionNames.sectionName);

    const lineAndPointsFeatures: Feature[] = [
      {
        type: 'Feature',
        id: `${FeatureTypeEnum.AUTOMATON_SECTION_LINE}-${automatonsSectionNames.sectionName}`,
        properties: {
          id: automatonsSectionNames.sectionName,
          featureType: FeatureTypeEnum.AUTOMATON_SECTION_LINE,
          value: automatonsSectionNames,
          color: getSectionLineColor(sectionData, sectionStatus),
          selectOptionLabel: automatonsSectionNames.sectionName,
        },
        geometry: { type: 'LineString', coordinates: sectionPositions },
      },
    ];

    lineAndPointsFeatures.push(
      toLinePointsFeature(
        automatonsSectionNames,
        [sectionPositions[0], sectionPositions[positionsCount - 1]],
        hexColor[ColorLabelEnum.WHITE],
      ),
    );

    if (positionsCount === 2) {
      return lineAndPointsFeatures;
    }

    return [
      ...lineAndPointsFeatures,
      toLinePointsFeature(
        automatonsSectionNames,
        sectionPositions.slice(1, positionsCount - 1),
        getSectionLineColor(sectionData, sectionStatus),
      ),
    ];
  });

export const toPortalFeatures = (portals: AutomatonPortalWithStatus[]): Feature[] => {
  return portals.map((portal) => {
    const position = portal.position;
    const isOpened = portal.opened;
    return {
      type: 'Feature',
      id: `${FeatureTypeEnum.PERIMETER_PORTAL}-${portal.id}`,
      properties: {
        featureType: FeatureTypeEnum.PERIMETER_PORTAL,
        value: portal.name,
        icon: `icSymb-gate-wh-L-${isOpened ? 'opened' : 'closed'}`,
        textField: portal.name,
        tooltipAnchor: 'pointer',
        showTooltip: true,
      },
      geometry: {
        type: 'Point',
        coordinates: toLonLatArray(position),
      },
    } as Feature;
  });
};

export const toAutomatonSectionIconFeatures = (
  sections: AutomatonSectionData[],
  statuses: AutomatonSectionStatus[],
  portals: AutomatonPortalWithStatus[],
  zoom: number | undefined,
): Feature[] => {
  const features = sections.flatMap((sectionData) => {
    const name = getSectionName(sectionData.section);
    const sectionCenter = toLonLatArray(getSectionCenterCoordinates(sectionData.section, zoom), false)!;
    const sectionStatus = statuses.find((status) => status.name === name);
    const iconStatus = getSectionIconStatus(sectionData, sectionStatus?.effectiveStatus ?? null);
    return [
      toAutomatonSectionIcons(sectionData, sectionCenter, iconStatus, sectionStatus?.specificState ?? null),
      toAutomatonSectionLabelFeature(sectionData, sectionCenter),
    ];
  });

  if (portals.length) {
    features.push(...toPortalFeatures(portals));
  }

  return features;
};
